import { useEffect, useId, useRef, useState } from 'react';
import { useT } from '../../i18n';
import { Icon } from './Icon';
import { Popover } from './Popover';
import styles from './BirthdatePicker.module.css';

interface BirthdatePickerProps {
  value: string;
  onChange?: (value: string) => void;
  label: string;
  name?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Kurzer Hinweis unter dem Feld (wie FormField.helpText). */
  hint?: string;
  required?: boolean;
  /** 'form' = Beschriftung wie FormField (Anmeldung, Konto, Pflichtangabe); Standard = Einstiegs-Chat. */
  variant?: 'chat' | 'form';
  /** Fokussiert den Auslöser und scrollt ihn in den Blick (Einstiegs-Chat). */
  autoFocus?: boolean;
}

function parseDate(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}
function dateValue(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** App-language calendar: no dependency on the browser/OS date-picker locale. */
export function BirthdatePicker({ value, onChange, label, name, disabled, invalid, hint, required, variant = 'chat', autoFocus }: BirthdatePickerProps) {
  const { lang, t } = useT();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const valueId = useId();
  const hintId = useId();
  const [open, setOpen] = useState(false);
  const today = new Date();
  const minYear = today.getFullYear() - 120;
  const selected = parseDate(value);
  // Only the viewport starts 16 years back. No date is silently submitted.
  const initial = selected ?? new Date(today.getFullYear() - 16, today.getMonth(), 1);
  const [view, setView] = useState({ year: initial.getFullYear(), month: initial.getMonth() });
  const lastDay = new Date(view.year, view.month + 1, 0).getDate();
  const offset = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
  const monthFormatter = new Intl.DateTimeFormat(lang, { month: 'long' });
  const fullFormatter = new Intl.DateTimeFormat(lang, { dateStyle: 'long' });
  const weekFormatter = new Intl.DateTimeFormat(lang, { weekday: 'short' });
  const numberFormatter = new Intl.NumberFormat(lang, { useGrouping: false });
  const todayValue = dateValue(today.getFullYear(), today.getMonth(), today.getDate());
  const isCurrentMonth = view.year === today.getFullYear() && view.month === today.getMonth();

  // Wie das frühere native Feld im Einstiegs-Chat: Auslöser fokussieren und die
  // Antwortzeile in den Blick scrollen (die Tastatur öffnet sich dabei nicht).
  useEffect(() => {
    if (!autoFocus) return undefined;
    const handle = window.setTimeout(() => {
      anchorRef.current?.focus({ preventScroll: true });
      anchorRef.current?.scrollIntoView?.({ block: 'end', inline: 'nearest' });
    }, 50);
    return () => window.clearTimeout(handle);
  }, [autoFocus]);
  const choose = (next: string) => { onChange?.(next); setOpen(false); anchorRef.current?.focus(); };
  const moveMonth = (delta: number) => {
    const date = new Date(view.year, view.month + delta, 1);
    setView({ year: date.getFullYear(), month: date.getMonth() });
  };
  return <div className={variant === 'form' ? `${styles.root} ${styles.form}` : styles.root}>
    <span id={titleId} className={styles.label}>{label}{required ? <span aria-hidden="true"> *</span> : null}</span>
    <button ref={anchorRef} type="button" className={styles.trigger} disabled={disabled}
      aria-labelledby={`${titleId} ${valueId}`} aria-describedby={hint ? hintId : undefined}
      aria-expanded={open} aria-haspopup="dialog" aria-invalid={invalid ? true : undefined} aria-required={required ? true : undefined}
      onClick={() => { setView({ year: initial.getFullYear(), month: initial.getMonth() }); setOpen(true); }}>
      <span id={valueId}>{selected ? fullFormatter.format(selected) : t('date_picker.choose')}</span>
      <Icon name="type-calendar" size={18} />
    </button>
    {hint ? <span id={hintId} className={styles.hint}>{hint}</span> : null}
    {name ? <input type="hidden" name={name} value={value} /> : null}
    {/* Der Dialog-Name sitzt am Kalender selbst; die Popover-Hülle bleibt neutral. */}
    <Popover open={open} onClose={() => setOpen(false)} anchorRef={anchorRef} role="presentation" className={styles.calendarPopover}>
      <div className={styles.calendar} role="dialog" aria-labelledby={titleId}>
        <div className={styles.controls}>
          <button type="button" aria-label={t('date_picker.previous_month')} disabled={view.year === minYear && view.month === 0} onClick={() => moveMonth(-1)}><Icon name="chevron-left" directional size={18} /></button>
          <select aria-label={t('date_picker.month')} value={view.month} onChange={event => setView({ ...view, month: Number(event.target.value) })}>
            {Array.from({ length: 12 }, (_, month) => <option key={month} value={month} disabled={view.year === today.getFullYear() && month > today.getMonth()}>{monthFormatter.format(new Date(2000, month, 1))}</option>)}
          </select>
          <select aria-label={t('date_picker.year')} value={view.year} onChange={event => {
            const year = Number(event.target.value);
            // Im laufenden Jahr gibt es keine zukünftigen Monate: nie in einen komplett gesperrten Monat springen.
            setView({ year, month: year === today.getFullYear() ? Math.min(view.month, today.getMonth()) : view.month });
          }}>
            {Array.from({ length: 121 }, (_, i) => today.getFullYear() - i).map(year => <option key={year} value={year}>{numberFormatter.format(year)}</option>)}
          </select>
          <button type="button" aria-label={t('date_picker.next_month')} disabled={isCurrentMonth} onClick={() => moveMonth(1)}><Icon name="chevron-right" directional size={18} /></button>
        </div>
        <div className={styles.days}>
          {Array.from({ length: 7 }, (_, day) => <span className={styles.weekday} key={`weekday-${day}`}>{weekFormatter.format(new Date(2024, 0, 1 + day))}</span>)}
          {Array.from({ length: offset }, (_, i) => <span key={`empty-${i}`} />)}
          {Array.from({ length: lastDay }, (_, i) => i + 1).map(day => {
            const next = dateValue(view.year, view.month, day);
            return <button key={day} type="button" aria-label={fullFormatter.format(new Date(view.year, view.month, day))}
              aria-pressed={next === value} disabled={next > todayValue} onClick={() => choose(next)}>{numberFormatter.format(day)}</button>;
          })}
        </div>
        <div className={styles.footer}>
          <button type="button" onClick={() => choose('')}>{t('date_picker.reset')}</button>
          <button type="button" onClick={() => { setOpen(false); anchorRef.current?.focus(); }}>{t('common.close')}</button>
        </div>
      </div>
    </Popover>
  </div>;
}
